import React from 'react';
import { Box, Typography, Radio, Button, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { convertStringToTitleCase, DEFAULT_FUNCTION, parseAmount } from '../../utils/constant';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DollarIcon from '@mui/icons-material/MonetizationOn';
import { useTranslation } from 'react-i18next';
import { frenchMessages } from '../../utils/Messages/FR-DA-MSG';
import { englishMessages } from '../../utils/Messages/EN-US-MSG';
import { ErrorHandler, ErrorNotifier } from '../Error';

interface Detail {
    title: string;
    icon: JSX.Element;  
}

interface Feature {
    application: keyof typeof applicationStatus;
    commercialName?: string;
}

interface BrandedFeature {
    subCode: string;
    commercialName: string;
}

interface Props {
    brandData: any;
    selectedOption: any
    handleRadioChange: any
    radioBtnCount: any
    recommended: any
    selectedCardTotal: any,
    pricingCall:(id:null|Object)=> void;
    language:string;
    processAmountAsPerPaxCount(amount: number): number;
    handleSelectClick:( id: string, brandData ) => void;
    callPriceAPI: (flag: boolean) => void;
}

const applicationStatus = {
    DisplayedButNotOffered: "Not offered",
    NotOffered: "Not offered",
    ServiceIsFree: "Free",
    Chargeable: "Chargeable"
};

const FareTypeCrads: React.FC<Props> = ({
    brandData,
    selectedOption,
    handleRadioChange,
    radioBtnCount,
    recommended,
    selectedCardTotal,
    pricingCall,
    language,
    processAmountAsPerPaxCount,
    handleSelectClick,
    callPriceAPI
}) => {
    const { t } = useTranslation();
    const {data: {brandFeatures}} = brandData;
    const getProcessesBrandedFare = (brandedFeatures: BrandedFeature[]) => {
        const subCodeMappings: Record<string, BrandedFeature> = {};
        const commercialNameMappings: Record<string, BrandedFeature> = {};
        brandedFeatures.forEach((feature) => {
            if (!subCodeMappings[feature.subCode]) {
                subCodeMappings[feature.subCode] = feature;
            }
            if (!commercialNameMappings[feature.commercialName]) {
                commercialNameMappings[feature.commercialName] = feature;
            }
        });
    
        return { commercialNameMappings, subCodeMappings };
    };
    
    const getSymbol = (feature: Feature) => {
        if (feature.application) {
            switch (feature.application) {
                
                case 'DisplayedButNotOffered':
                case 'NotOffered':
                        return (<CloseIcon className='uncheckIcon'/>)
                        
                case "ServiceIsFree":
                    return (<CheckIcon className='checkIcon'/>)
                case "Chargeable":
                    return (<DollarIcon className='dollarIcon'/>)
                            
                default:
                    return null;
            }
        }
        return null;
    };
    
    const RenderFeature = ({ feature, name = "" }: any) => {
        let feat = feature?.commercialName?.toUpperCase();
                                    
        if (feature && (language == "fr" && frenchMessages[feat] || language == "en")) {
            return (
                <List className='fareType_IconWithTitle'>

                    <ListItem>
                        <ListItemAvatar>
                            {getSymbol(feature)}
                        </ListItemAvatar>
                        <ListItemText primary={convertStringToTitleCase(name) || convertStringToTitleCase(t(feat))} />
                    </ListItem>

                </List>
            );
        }
        return null;
    };

    const handleSelect = async (flag: boolean = false) => {
        let selectedId = brandData?.id;
        const { fareBasisDetails } = brandData;
        let key = "";
        let allKeysAreSame = true

        fareBasisDetails?.forEach(({ fareDetails }) => {
            if (!key) {
                key = fareDetails?.[0]?.brandCode;
            }
            if (allKeysAreSame) {
                allKeysAreSame = fareDetails.every(elem => elem.brandCode === key)
            }
        })

        if((allKeysAreSame) || flag){
            let obj={
                "fareId":selectedId
            }
            let res = await pricingCall(obj)
        }else{
            handleSelectClick(selectedId, brandData);
            ErrorHandler('Important!', "The upgraded fare may not be available for all legs of your journey. Please review the fare details before proceeding.", null, 'Ok', 'warning', 'alertModal');
        }
    }

    
    const {commercialNameMappings, subCodeMappings} = getProcessesBrandedFare(brandFeatures)

      const groupBySectionTitle = (features:any) => {
        return features.reduce((acc:any, feature:any) => {
          const { sectionTitle } = feature;
          if (!sectionTitle) return acc;

          if (!acc[sectionTitle]) {
            acc[sectionTitle] = [];
          }
          acc[sectionTitle].push(feature);
          return acc;
        }, {});
      };
      const groupedFeatures = groupBySectionTitle(brandFeatures);

    let oneCabinBag = commercialNameMappings["1 CABIN BAG UP TO 8KG"];
    let twoCabinBag = commercialNameMappings["2 CABIN BAGS UP TO 8KG"];
    let subPrice:any=Number(brandData.total-selectedCardTotal).toFixed(2)
    let chargedBaggages = brandData?.chargedBaggage
    let freeBaggageData = brandData?.freeBaggage?.[0]?.quantity
    let freeBaggageDataWeight = brandData?.freeBaggage?.[0]?.weight;
    let carryOnBaggageData = brandData?.carryOnBaggages?.[0]?.quantity
    let carryOnBaggageDataWeight = brandData?.carryOnBaggages?.[0]?.weight;
    let carryOnBaggageUnit = brandData?.carryOnBaggages?.[0]?.unit;
    return (
        <>
            <Box className={"fareTypeCrads " +
                ("fareTypeActive ") + (recommended && " fareTypeRecommended") + ((radioBtnCount == 0) ? " firstactive" : "")
            }>
                {recommended ? <Typography className='fareType_Recommended'>{t('Recommended')}</Typography> : null}
                <Box className="fareType_Top">
                    <Box className="fareTypePrice">
                        {brandData.brandDifference ? <Typography className='serResult-seatInfo serResult-seatFlight' component={'span'} style={{color: "#005279"}}>+ ${brandData.brandDifference}</Typography> : null}
                        <Typography className='inOutTop_mainTitle' variant='h6'>$ {parseAmount(processAmountAsPerPaxCount(brandData.total))}</Typography>
                        <Box>
                         <Typography className='fareType_title' component={'span'}>{t(brandData?.brandName)}  |</Typography>
                         <Typography className='fareType_cabin' component={'span'}> {t(brandData?.cabin)}</Typography>
                        </Box>
                    </Box>
                    <Box>
                        {/* <Radio
                            checked={selectedOption === radioBtnCount}
                            onChange={() => handleRadioChange(radioBtnCount)}
                            value={radioBtnCount}
                            name="radio-buttons"
                        /> */}
                    </Box>
                </Box>
                <Box className="fareType_Content">
                    <Box className="fareType_top">
                        
                        
                    </Box>
                    {
                        (oneCabinBag || twoCabinBag || brandData?.freeBaggage?.length || chargedBaggages?.length || brandData?.carryOnBaggages?.length ) ?
                            <>
                                <Typography className='fareType_subTitle' variant='h3'>{t('Bags')}</Typography>
                                {(freeBaggageData !== undefined || freeBaggageDataWeight !== undefined) &&
                                    <>
                                        <List className='fareType_IconWithTitle'>
                                            <ListItem>
                                                <ListItemAvatar>
                                                    <CheckIcon className='checkIcon' />
                                                </ListItemAvatar>
                                                <ListItemText primary={t("Checked Bags Included")} />
                                                {freeBaggageData === 0 && freeBaggageDataWeight === 0 ?
                                                    <>
                                                        {freeBaggageData}

                                                    </>
                                                    :
                                                    <>
                                                        {freeBaggageData
                                                            ? freeBaggageData
                                                            : freeBaggageDataWeight + " Kg"}
                                                    </>
                                                }
                                            </ListItem>
                                        </List>
                                    </>
                                }
                                {chargedBaggages &&
                                    chargedBaggages.map(
                                        (bagData: any) => {
                                            let firstPiece = bagData && bagData.firstPiece;
                                            let lastPiece = bagData && bagData.lastPiece;
                                            let bagAmount = bagData && bagData.amount && parseAmount(bagData.amount);
                                            let chargeBaggageValue;

                                            if (lastPiece) {
                                                if (firstPiece === lastPiece)
                                                    chargeBaggageValue = t("Fee for Additional Bag") + lastPiece;

                                                if (firstPiece !== lastPiece)
                                                    chargeBaggageValue = t("Fee for Additional Bag") + firstPiece +"-" +lastPiece;

                                            }
                                            if (!lastPiece) {
                                                chargeBaggageValue = t("Fee for Additional Bag") + firstPiece;
                                            }

                                            if (firstPiece || lastPiece)
                                                return (
                                                    <>
                                                        <List className='fareType_IconWithTitle'>
                                                            <ListItem>
                                                                <ListItemAvatar>
                                                                    {<DollarIcon className='dollarIcon' />}
                                                                </ListItemAvatar>
                                                                <ListItemText primary={(chargeBaggageValue)} />
                                                                $ {bagAmount}
                                                            </ListItem>
                                                        </List>
                                                    </>
                                                );
                                        }
                                    )
                                }
                                {oneCabinBag && <RenderFeature feature={oneCabinBag} />}
                                {twoCabinBag && <RenderFeature feature={twoCabinBag} />}

                                {(carryOnBaggageData !== undefined || carryOnBaggageDataWeight !== undefined) &&
                                    <>
                                        <List className='fareType_IconWithTitle'>
                                            <ListItem>
                                                <ListItemAvatar>
                                                    <CheckIcon className='checkIcon' />
                                                </ListItemAvatar>
                                                <ListItemText primary={t("Carry-On")} />
                                                {carryOnBaggageData === 0 && carryOnBaggageDataWeight === 0 ?
                                                    <>
                                                        {carryOnBaggageData}

                                                    </>
                                                    :
                                                    <>
                                                        {carryOnBaggageData
                                                            ? carryOnBaggageData
                                                            : carryOnBaggageDataWeight + ` ${carryOnBaggageUnit}`}
                                                    </>
                                                }
                                            </ListItem>
                                        </List>
                                    </>
                                }

                            </>
                            :
                            null
                    }


                    {groupedFeatures &&
                        (!!Object.keys(groupedFeatures).find(sectionTitle =>
                            groupedFeatures[sectionTitle].find(feature => {
                                console.log("yash",sectionTitle,feature,frenchMessages[feature?.commercialName?.toUpperCase()], language)
                                const feat = feature?.commercialName?.toUpperCase();
                                return ((language === "fr" && frenchMessages[feat]) || language === "en");
                            })
                        )) &&
                        Object.keys(groupedFeatures).map((sectionTitle: any) => (
                        <>
                            <Typography className='fareType_subTitle' variant='h3'>{t(sectionTitle)}</Typography>
                            <List className='fareType_IconWithTitle'>
                                {groupedFeatures[sectionTitle].map((feature: any) => {
                                    let feat = feature?.commercialName?.toUpperCase();
                                    return (
                                        (language == "fr" && frenchMessages[feat] || language == "en") ?
                                            <ListItem key={feature.commercialName}>
                                                <ListItemAvatar>
                                                    {getSymbol(feature)}
                                                </ListItemAvatar>
                                                <ListItemText primary={convertStringToTitleCase(t(feat))} />
                                            </ListItem>
                                            : null
                                    )
                                })}
                            </List>
                        </>
                    ))}
                </Box>
                <Box className="fareType_btnSection">
                    <Button className={"fareTypeBtn " + ( "fareTypeBtnActive") + ((radioBtnCount == 0) ? " firstactivebtn" : "")} onClick={()=>handleSelect(false)}>{((radioBtnCount == 0) ? t('Continue') : t('Select'))}</Button>
                </Box>
            </Box>
            <ErrorNotifier handleCloseFunc={()=>callPriceAPI(true)} />
        </>
    );
}

export default FareTypeCrads;
